import { Link } from 'react-router-dom';
import { useState } from 'react';

export function Logo({ className = 'h-10' }) {
  return <img src="/brand/brayo-logo-alpha.png" alt="Brayo Records" className={`w-auto ${className}`} />;
}

export function PlayIcon({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5.5v13l11-6.5-11-6.5z" />
    </svg>
  );
}

export function ArtistCard({ artist, index = 0 }) {
  return (
    <Link
      to={`/artists/${artist.slug}`}
      data-testid={`artist-card-${artist.slug}`}
      className="group block"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-brayo-panel">
        <img
          src={artist.image_url}
          alt={artist.name}
          loading={index < 4 ? 'eager' : 'lazy'}
          className="h-full w-full object-cover transition-all duration-700 ease-out group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-brayo-blue/0 transition-colors duration-500 group-hover:bg-brayo-blue/10" />
      </div>
      <div className="flex items-baseline justify-between border-b border-brayo-line py-3">
        <div>
          <h3 className="display-title text-lg text-brayo-charcoal transition-colors group-hover:text-brayo-blue md:text-xl">
            {artist.name}
          </h3>
          <p className="meta-label mt-1 text-brayo-dim">{artist.genre}</p>
        </div>
        <svg className="h-4 w-4 shrink-0 text-brayo-blue opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:opacity-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      </div>
    </Link>
  );
}

export function ReleaseCard({ release, index = 0, large = false }) {
  return (
    <Link
      to={`/releases/${release.slug}`}
      data-testid={`release-card-${release.slug}`}
      className={`group block ${large ? 'md:col-span-2 md:row-span-2' : ''}`}
    >
      <div className="relative aspect-square overflow-hidden bg-brayo-panel">
        <img
          src={release.cover_url}
          alt={`${release.title} — ${release.artist}`}
          loading={index < 4 ? 'eager' : 'lazy'}
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
        />
        <div className="absolute right-3 top-3 flex gap-2">
          <span className="meta-label border border-white/20 bg-brayo-black/60 px-2 py-1 text-white backdrop-blur-sm">{release.type}</span>
          {release.cat_number && (
            <span className="meta-label hidden border border-white/20 bg-brayo-black/60 px-2 py-1 text-brayo-blue backdrop-blur-sm sm:block">
              {release.cat_number}
            </span>
          )}
        </div>
        <div className="absolute inset-x-0 bottom-0 flex translate-y-full items-center justify-between bg-brayo-blue px-4 py-2.5 transition-transform duration-400 ease-out group-hover:translate-y-0">
          <span className="meta-label text-white">Listen</span>
          <PlayIcon className="h-3.5 w-3.5 text-white" />
        </div>
      </div>
      <div className="border-b border-brayo-line py-3">
        <h3 className={`display-title text-brayo-charcoal transition-colors group-hover:text-brayo-blue ${large ? 'text-2xl md:text-3xl' : 'text-lg'}`}>
          {release.title}
        </h3>
        <p className="meta-label mt-1 text-brayo-dim">
          {release.artist} — {release.release_date}
        </p>
      </div>
    </Link>
  );
}

export function VideoCard({ video, onPlay, large = false }) {
  return (
    <button
      data-testid={`video-card-${video.id || video.youtube_id || video.vimeo_id}`}
      onClick={() => onPlay(video)}
      className="group relative block w-full overflow-hidden bg-brayo-panel text-left"
    >
      <div className={`relative ${large ? 'aspect-video' : 'aspect-video'}`}>
        <img src={video.thumbnail_url} alt={video.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
        <div className="absolute inset-0 bg-brayo-black/20 transition-colors duration-300 group-hover:bg-brayo-black/0" />
        <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-brayo-black/50 text-white backdrop-blur-sm transition-all duration-300 group-hover:scale-110 group-hover:border-brayo-blue group-hover:bg-brayo-blue">
          <PlayIcon className="ml-0.5 h-5 w-5" />
        </span>
      </div>
      <div className="border-b border-brayo-line py-3">
        <h3 className={`leading-snug text-brayo-charcoal transition-colors group-hover:text-brayo-blue ${large ? 'text-xl md:text-2xl font-bold' : 'text-base font-bold'}`}>
          {video.title}
        </h3>
        {video.credit && <p className="meta-label mt-1 text-brayo-dim">{video.credit}</p>}
      </div>
    </button>
  );
}

export function PageHero({ overline, title, children }) {
  return (
    <div className="border-b border-brayo-line pb-10 pt-32 md:pb-14 md:pt-44">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <p className="meta-label mb-4 text-brayo-blue">{overline}</p>
        <h1 className="display-title text-5xl text-brayo-charcoal sm:text-6xl lg:text-8xl">{title}</h1>
        {children}
      </div>
    </div>
  );
}